/* eslint-disable max-statements */
import { push } from "connected-react-router";
import { isEqual, cloneDeep } from "lodash";
import { batch, useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "redux-undo";
import { generate } from "short-uuid";
import { dashboardTypeToFilters } from "../filters";
import { MoveDirection } from "../left-pane/types";
import { useCreateAnalysisMutation } from "../store/api";
import { PageMargin, PageSize, PageType, workspaceEntityTypeDataToEntityTypeMap } from "../store/types";
import { AssetClass } from "api/ux/src";
import { getAnalysisSections } from "components/modal/download-pdf/request-helpers";
import { ModalName } from "components/modal/types";
import { useCurrentDashboardType, useDashboardFullPathInformations } from "components/workspaces/hooks/use-current-dashboard";
import { appStore } from "index";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { sectionSelector } from "store/cross-slice";
import { openModal } from "store/modal";
import { PdfModalType } from "store/modal/pdf-modal-types";
import { createSectionFrom, setDesignerMode } from "store/template";
import { getCurrentLocalTime } from "common/date-utils";
export const getFiltersByCurrentDashboardType = (currentDashboardType) => dashboardTypeToFilters.get(currentDashboardType) ?? [];
export const getSections = (analysis) => (analysis.sections ?? []).reduce((result, current) => {
    if (current.id && current.name) {
        result[current.id] = current.name;
    }
    return result;
}, {});
export const getPanels = (analysesList) => analysesList?.map((analysis, index) => ({
    id: index.toString(),
    name: analysis.name ?? "",
    sections: getSections(analysis)
})) ?? [];
export const getDesignerPayload = (analysis) => ({
    analysis,
    id: analysis.id,
    sections: analysis.sections?.reduce((prev, curr) => {
        if (curr.id) {
            prev[curr.id] = curr;
        }
        return prev;
    }, {})
});
export const onAddAnalysisClicked = async (currentWorkspace, entityType, createAnalysis) => {
    if (currentWorkspace.id) {
        const sectionLayouts = [];
        const newAddedSectionId = generate();
        const section = [
            {
                id: newAddedSectionId,
                margin: PageMargin.UNDEFINED_PAGE_MARGIN,
                name: language.en.newPage,
                orderNumber: 0,
                pageSize: PageSize.UNDEFINED_PAGE_SIZE,
                requiredModules: [],
                type: PageType.SCREEN,
                widgets: { all: sectionLayouts }
            }
        ];
        const newAnalysis = {
            analysis: {
                applicableAssetClass: AssetClass.ALL,
                applicableAssetClasses: [AssetClass.ALL],
                defaultFilters: [],
                filters: getFiltersByCurrentDashboardType(workspaceEntityTypeDataToEntityTypeMap(entityType)),
                isDefault: false,
                name: language.en.newAnalysis,
                sections: section,
                workspaceId: currentWorkspace.id
            }
        };
        await createAnalysis(newAnalysis);
        return newAnalysis;
    }
    return undefined;
};
export const moveAnalysis = async (direction, panelIndex, analysesList, updateAnalyses) => {
    const analysisToMove = analysesList[panelIndex];
    const analysisToSwitch = direction === MoveDirection.Down ? analysesList[panelIndex + 1] : analysesList[panelIndex - 1];
    const updateRequest = {
        analyses: [analysisToMove, analysisToSwitch]
    };
    await updateAnalyses(updateRequest);
};
export const useDuplicateAnalysis = () => {
    const [createAnalysis] = useCreateAnalysisMutation();
    const { selectedType } = useDashboardFullPathInformations();
    const template = useSelector((state) => state.entities.Template)[selectedType];
    const sections = useSelector((state) => state.entities.Section);
    const widgets = useSelector((state) => state.entities.Widget, isEqual);
    const handleDuplicateAnalysis = async (analysisToEdit) => {
        const { id: currentAnalysisId, workspaceId } = analysisToEdit;
        if (!analysisToEdit) {
            return;
        }
        let duplicateAnalysisRequest = {};
        if (analysisToEdit.isDefault) {
            const newAnalysis = cloneDeep(analysisToEdit), newSections = [], newWidgets = [];
            newAnalysis.sections?.forEach((sec, index) => {
                const { section: newSection, widgets: newWidget } = createSectionFrom(sectionSelector(sec.id || "")(appStore.getState()), widgets);
                if (newSection) {
                    newSections.push(newSection);
                    sec.id = generate();
                    sec.name = `${newSection.name} (Copy)`;
                    sec.orderNumber = analysisToEdit.sections?.[index].orderNumber;
                    sec.widgets = { all: sections?.[analysisToEdit.sections?.[index].id || ""].widgets?.all.map((widget, i) => ({
                            ...widget,
                            id: newWidget?.[i].id
                        })) };
                }
                if (newWidget) {
                    const widgetsWithPageId = newWidget.map((widget) => ({
                        ...widget,
                        pageId: sec.id
                    }));
                    newWidgets.push(...widgetsWithPageId);
                }
            });
            newAnalysis.name = `${analysisToEdit.name} (Copy)`;
            newAnalysis.isDefault = false;
            template.analysis?.push(newAnalysis);
            duplicateAnalysisRequest = {
                analysis: {
                    ...newAnalysis,
                    id: "",
                    widgets: newWidgets,
                    workspaceId
                }
            };
        }
        else {
            duplicateAnalysisRequest = {
                analysis: {
                    id: currentAnalysisId || generate(),
                    name: `${analysisToEdit.name} (Copy)`,
                    sections: analysisToEdit.sections,
                    workspaceId: workspaceId || ""
                }
            };
        }
        await createAnalysis(duplicateAnalysisRequest);
    };
    return { handleDuplicateAnalysis };
};
export const usePdfModalAnalysisSections = (modalMode, analysisIndex, analysesList) => {
    const currentSection = useSelector((state) => state.currentDashboard.currentSection, isEqual), dashboardType = useCurrentDashboardType();
    let analysis = analysesList?.find((a) => a.sections?.some((s) => s.id === currentSection));
    if (modalMode === PdfModalType.Analysis && analysisIndex !== undefined) {
        analysis = analysesList?.[analysisIndex];
    }
    const sections = analysis?.sections;
    if (!sections || sections.length === 0) {
        return [];
    }
    let analysisSections = getAnalysisSections(dashboardType, sections);
    if (analysisSections.length === 1) {
        analysisSections = analysisSections.map((section) => ({
            ...section,
            isSelected: true
        }));
    }
    else {
        analysisSections = analysisSections.map((section) => ({
            ...section,
            isSelected: section.id === currentSection
        }));
    }
    return analysisSections;
};
export const useHandleOnAddAnalyses = (currentWorkspace, entityInfo, createAnalysis, accessToSharedFolder) => {
    const dispatch = useDispatch();
    const handleOnAddAnalyses = async () => {
        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.AddAnalysis, properties: { LocalTimestamp: getCurrentLocalTime() } });
        if (entityInfo.type === undefined || currentWorkspace === undefined || (!accessToSharedFolder && !currentWorkspace.isPrivate)) {
            return;
        }
        const newAnalysis = {
            analysis: {
                applicableAssetClass: AssetClass.ALL,
                applicableAssetClasses: [AssetClass.ALL],
                defaultFilters: [],
                filters: getFiltersByCurrentDashboardType(workspaceEntityTypeDataToEntityTypeMap(entityInfo.type)),
                isDefault: false,
                name: language.en.newAnalysis,
                sections: [],
                workspaceId: currentWorkspace.id
            }
        };
        await createAnalysis(newAnalysis);
        batch(() => {
            dispatch(push(`/${entityInfo.type?.toLocaleLowerCase()}/${entityInfo.id}/${currentWorkspace.id}/`));
            dispatch(setDesignerMode(getDesignerPayload(newAnalysis?.analysis || {})));
            dispatch(openModal({
                content: {
                    id: "addSection",
                    modalName: ModalName.AddPage
                },
                title: language.en.modal.addPage.title,
                type: "action"
            }));
            dispatch(ActionCreators.clearHistory());
        });
    };
    return { handleOnAddAnalyses };
};
